/**
 * Drill audio: the right/wrong sounds and the question/answer speech
 * (moved verbatim from StepView.vue). The timer runs while the question is
 * spoken (D-3); presentQuestion times a new try from 0. The time spent
 * speaking the question is kept apart (questionAudioMs) so a time limit
 * never counts it.
 */
import { ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import { useSound } from 'shared/composables/useSound/useSound';
import { useTts } from 'src/shared/composables/useTts/useTts';
import rightSfx from 'assets/sounds/rightSfx.mp3';
import wrongSfx from 'assets/sounds/wrongSfx.mp3';

/** The strategy flags audio reads (a DrillStrategy satisfies it). */
export interface DrillAudioStrategy {
  readonly speaksQuestion: boolean;
  readonly speaksAnswer: boolean;
}

export interface DrillAudioTimer {
  start: () => void;
  reset: () => void;
}

export interface UseDrillAudioOptions {
  strategy: MaybeRefOrGetter<DrillAudioStrategy | null | undefined>;
  isQuestionHearingEnabled: MaybeRefOrGetter<boolean | undefined>;
  isCompletedStep: MaybeRefOrGetter<boolean>;
  /** false once the view is gone: nothing may run after that (BUG-24). */
  isActive: () => boolean;
  timer: DrillAudioTimer;
  /** The text of the current question (StepView's drillTextContent). */
  questionText: MaybeRefOrGetter<string>;
}

export interface UseDrillAudioReturn {
  playQuestion: () => Promise<void>;
  presentQuestion: () => Promise<void>;
  playAnswer: (answer: string) => Promise<void>;
  /** Time the current try spent speaking the question (replays included). */
  questionAudioMs: Readonly<Ref<number>>;
  /** The question is being spoken right now. */
  isSpeakingQuestion: Readonly<Ref<boolean>>;
  /** Forget the question audio time (the try is timed from 0 again). */
  resetQuestionAudio: () => void;
  notifyRight: () => void;
  notifyWrong: () => void;
}

export function useDrillAudio({
  strategy,
  isQuestionHearingEnabled,
  isCompletedStep,
  isActive,
  timer,
  questionText,
}: UseDrillAudioOptions): UseDrillAudioReturn {
  const { play: notifyRight } = useSound(rightSfx);
  const { play: notifyWrong } = useSound(wrongSfx);

  const { utter } = useTts({ lang: 'en' });

  const questionAudioMs = ref(0);
  const isSpeakingQuestion = ref(false);

  function resetQuestionAudio() {
    questionAudioMs.value = 0;
  }

  /** Speaks the current question. The timer runs while it speaks (D-3). */
  async function playQuestion() {
    if (!isActive() || !toValue(isQuestionHearingEnabled)) {
      return;
    }

    if (toValue(strategy)?.speaksQuestion && !toValue(isCompletedStep)) {
      timer.start();
      const startedAt = Date.now();
      isSpeakingQuestion.value = true;
      try {
        await utter(toValue(questionText), { delay: 800 });
      } catch (error) {
        console.error('Error playing question:', error);
      } finally {
        isSpeakingQuestion.value = false;
        questionAudioMs.value += Date.now() - startedAt;
      }
    }
  }

  /**
   * The student can answer a new try: time it from 0, including the time spent
   * listening to the question (D-3, D-6).
   */
  async function presentQuestion() {
    if (!isActive() || toValue(isCompletedStep)) return;
    timer.reset();
    resetQuestionAudio();
    timer.start();
    await playQuestion();
  }

  /** Speaks the correct answer (also on Reading-only drills, D-7); the timer stays stopped. */
  async function playAnswer(answer: string) {
    try {
      if (toValue(strategy)?.speaksAnswer) await utter(answer, { delay: 200 });
    } catch (error) {
      console.error('Error playing answer:', error);
    }
  }

  return {
    playQuestion,
    presentQuestion,
    playAnswer,
    questionAudioMs,
    isSpeakingQuestion,
    resetQuestionAudio,
    notifyRight,
    notifyWrong,
  };
}
